import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Routing } from '../../../../../../../util/routes.util';
import UpdateButton from '../../../../../../components/Button/templates/UpdateButton';
import { BackButton, ButtonVariant, DeleteButton } from '../../../../../../components/Button';
import { DraggableItemData } from '../../../../../Common/DraggableItem';
import { useRequest } from '../../../../../../../requests/common/requestHook';
import { ProductMasterDataExportConfigurationContext } from '../../../../../../../context/ProductMasterDataExportConfigurationContext';
import {
  ProductMasterDataExportConfiguration,
  ProductMasterDataExportConfigurationField,
  ProductMasterDataExportConfigurationInput,
} from '../../../../../../../types/product_master_data_export_configuration';
import { ProductMasterDataExportConfigurationRequest } from '../../../../../../../requests/productMasterDataExportConfiguration.request';
import ProductMasterDataExportConfigurationForm, {
  DraggableProductMasterDataExportFieldIdentifier,
} from '../Common/ProductMasterDataExportConfigurationForm';
import DeleteProductMasterDataExportConfigurationModal from './Modals/DeleteProductMasterDataExportConfigurationModal';
import { CompanyContext } from '../../../../../../../context/CompanyContext';

interface UpdateProductMasterDataExportConfigurationPaneProps {}

export default function UpdateProductMasterDataExportConfigurationPane({}: UpdateProductMasterDataExportConfigurationPaneProps) {
  const id = useParams()['id'] || '';
  if (!id) return null; // TODO: Entity not found

  const navigate = useNavigate();
  const { currentCompany } = useContext(CompanyContext);

  const {
    productMasterDataExportConfigurations,
    setProductMasterDataExportConfigurations,
    productMasterDataExportConfigurationsLoading,
  } = useContext(ProductMasterDataExportConfigurationContext);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const existing = productMasterDataExportConfigurations.get(id);
  const [input, setInput] = useState<ProductMasterDataExportConfigurationInput>(
    new ProductMasterDataExportConfigurationInput(
      existing || ProductMasterDataExportConfiguration.default(currentCompany.id),
    ),
  );

  useEffect(() => {
    if (!productMasterDataExportConfigurationsLoading && existing) {
      setInput(new ProductMasterDataExportConfigurationInput(existing));
    }
  }, [productMasterDataExportConfigurationsLoading]);

  const [update, { loading: updateLoading }] = useRequest(ProductMasterDataExportConfigurationRequest.update);

  const handleUpdate = async (
    configuration: ProductMasterDataExportConfigurationInput,
    items: DraggableProductMasterDataExportFieldIdentifier[],
  ) => {
    configuration.mapping = DraggableItemData.toMap<ProductMasterDataExportConfigurationField>(items);

    const result = await update({ data: [configuration.forUpdate()] });
    if (!result) return;

    const config = result[0];
    productMasterDataExportConfigurations.set(config.id, new ProductMasterDataExportConfiguration(config));
    setProductMasterDataExportConfigurations(new Map(productMasterDataExportConfigurations));
    navigate(Routing.productMasterData.settings.report.overview());
  };

  const footer = (
    configuration: ProductMasterDataExportConfigurationInput,
    items: DraggableProductMasterDataExportFieldIdentifier[],
  ) => {
    return (
      <div className='flex justify-between'>
        <div>
          <DeleteButton
            disabled={productMasterDataExportConfigurationsLoading || updateLoading}
            onClick={() => setOpenDeleteModal(true)}
          />
        </div>
        <div className='flex items-center gap-2'>
          <BackButton
            disabled={updateLoading}
            variant={ButtonVariant.secondary}
            onClick={() => navigate(Routing.stock.products.settings.report.overview())}
          />
          <UpdateButton
            loading={updateLoading}
            disabled={productMasterDataExportConfigurationsLoading || !configuration.filename || !configuration.name}
            onClick={() => handleUpdate(configuration, items)}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <DeleteProductMasterDataExportConfigurationModal
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        ids={new Set([id])}
      />
      <ProductMasterDataExportConfigurationForm
        loading={productMasterDataExportConfigurationsLoading}
        input={input}
        setInput={setInput}
        footer={footer}
      />
    </>
  );
}
